import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from '../utils/toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  axios.defaults.baseURL = API_BASE_URL;
  axios.defaults.withCredentials = true;

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const userInfo = localStorage.getItem('user-info');
      if (userInfo) {
        const parsedUser = JSON.parse(userInfo);
        setUser(parsedUser.user);

        // Set axios authorization header
        if (parsedUser.token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${parsedUser.token}`;
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('user-info');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);

      // First, authenticate with Google OAuth
      const googleResponse = await axios.get(`/auth/google?email=${credentials.email}`);

      if (googleResponse.data.email) {
        // If OTP is provided in response (for testing), verify it
        if (googleResponse.data.otp && credentials.otp) {
          const mfaResponse = await axios.post('/auth/verify-mfa', {
            email: credentials.email,
            otp: credentials.otp
          });

          if (mfaResponse.data.accessToken) {
            const userData = {
              user: mfaResponse.data.user,
              token: mfaResponse.data.accessToken,
              refreshToken: mfaResponse.data.refreshToken
            };

            localStorage.setItem('user-info', JSON.stringify(userData));
            axios.defaults.headers.common['Authorization'] = `Bearer ${mfaResponse.data.accessToken}`;
            setUser(mfaResponse.data.user);

            toast.success(`Welcome back, ${mfaResponse.data.user.first_name}!`);
            return { success: true };
          }
        } else {
          // OTP sent to email
          toast.success('OTP sent to your email. Please check and enter the OTP.');
          return { success: false, message: 'OTP_REQUIRED', email: googleResponse.data.email };
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (email, otp) => {
    try {
      setLoading(true);
      const response = await axios.post('/auth/verify-mfa', { email, otp });

      if (response.data.accessToken) {
        const userData = {
          user: response.data.user,
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken
        };

        localStorage.setItem('user-info', JSON.stringify(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
        setUser(response.data.user);

        toast.success(`Welcome back, ${response.data.user.first_name}!`);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'OTP verification failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const userInfo = localStorage.getItem('user-info');
      if (userInfo) {
        const { refreshToken } = JSON.parse(userInfo);
        await axios.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user-info');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      if (response.data.success) {
        const updatedUserInfo = JSON.parse(localStorage.getItem('user-info'));
        updatedUserInfo.user = { ...updatedUserInfo.user, ...response.data.user };
        localStorage.setItem('user-info', JSON.stringify(updatedUserInfo));
        setUser(updatedUserInfo.user);
        toast.success('Profile updated successfully');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  /** Merge partial fields into the logged-in user (e.g. after profile photo upload). */
  const patchUser = useCallback((partial) => {
    try {
      const raw = localStorage.getItem('user-info');
      if (!raw) return;
      const updatedUserInfo = JSON.parse(raw);
      updatedUserInfo.user = { ...updatedUserInfo.user, ...partial };
      localStorage.setItem('user-info', JSON.stringify(updatedUserInfo));
      setUser(updatedUserInfo.user);
    } catch (e) {
      console.error('patchUser failed:', e);
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    verifyOTP,
    logout,
    updateProfile,
    patchUser,
    isAuthenticated: !!user,
    isAdmin: user?.role?.toLowerCase() === 'admin',
    isHR: user?.role?.toLowerCase() === 'hr',
    isManager: user?.role?.toLowerCase() === 'manager',
    isAccountant: user?.role?.toLowerCase() === 'accountant',
    isTeamLead: user?.role?.toLowerCase() === 'team_lead',
    isEmployee: user?.role?.toLowerCase() === 'employee'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};